import _ from "lodash";
import React, { useEffect, useRef, useState } from 'react';
import BookingField from './BookingDrawerField.react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SabreLogo } from '../../../img/sabre.svg';
import { ReactComponent as AmadeusLogo } from '../../../img/amadeus.svg';
import {
    generateAmadeusGdsSegment,
    generateSabreGdsSegment,
    normalizeToSabreRoomType,
    generateAdditionForAmadeusGdsSegment,
} from "./helpers/utils";
import { ReactComponent as CopyIcon } from '../../../img/bookings/copy.svg';
import { ReactComponent as CheckIcon } from '../../../img/icons/check_symbol.svg';
import { ReactComponent as PencilIcon } from '../../../img/icons/pencil.svg';
import { Button, Radio, RadioGroup, FormControlLabel, FormControl, TextField } from "@mui/material";
import ExtrasTooltip from './ExtrasTooltip.react';
import Analytics from 'arbitrip-common/client/analytics';

const SELECTED_TYPE = {
    SABRE: 'Sabre',
    AMADEUS: 'Amadeus',
}

const BookingDrawerGDSComponent = ({ booking, profile }) => {
    // TODO: add translations for this page
    const { t } = useTranslation();

    if (!booking) {
        return null;
    }

	// BlueCube company. Additional login in Sabre
	const isBlueCubeCompany = _.get(profile, 'company.id') === '672cc929ad7a952c65f06a05';
	// Business Travel Centre company. Additional login in Sabre
	const isBTCCompany = _.get(profile, 'company.id') === '687f8e7f53c215ba5025f2ae';
	// Easy Avenues company. Additional login in Amadeus
	const isEACompany = _.get(profile, 'company.id') === '67c5d4ba95c1a1d6650bc8ab';

    const isAmadeusEnabled = _.get(profile, 'company.settings.enable_amadeus_gds_string_generation', false);
    const isSabroEnabled = _.get(profile, 'company.settings.enable_sabro_gds_string_generation', false);

    const selectedGDSType = isAmadeusEnabled && isSabroEnabled ? SELECTED_TYPE.SABRE : isAmadeusEnabled ? SELECTED_TYPE.AMADEUS : SELECTED_TYPE.SABRE;

	const [ copied, setCopied ] = useState(false);
	const [ copiedAddition, setCopiedAddition ] = useState(false);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ editedString, setEditedString ] = useState('');
    const [ gdsType, setGdsType ] = useState(selectedGDSType);
    const [ editedStringEA, setEditedStringEA ] = useState('');
    const textareaRef = useRef(null);

	const generatedString =
        gdsType === SELECTED_TYPE.SABRE
            ? generateSabreGdsSegment(booking, {
                  isBlueCubeCompany,
                  isBTCCompany,
              })
            : generateAmadeusGdsSegment(booking, { isEACompany });

    useEffect(() => {
        setEditedString(generatedString);
    }, [ generatedString, gdsType ]);

    useEffect(() => {
        if (isEditing) {
            autoResizeTextarea();
        }
    }, [ isEditing, gdsType, editedString ]);

    const autoResizeTextarea = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    };

    const handleCopyClick = async () => {
        try {
            await navigator.clipboard.writeText(isEditing ? editedString : generatedString);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
			Analytics.actions.interactions.gdsCopyAction({
				bookingId: booking.id,
				editedString: isEditing ? editedString : '',
				gdsType,
				generatedString,
				isEditing: isEditing,
				user: profile,
			});
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

 const generateAdditionAndCopy = async () => {
		try {
			const generatedAddition = generateAdditionForAmadeusGdsSegment(booking, editedStringEA?.trim());
			await navigator.clipboard.writeText(generatedAddition);
			setCopiedAddition(true);
			setTimeout(() => setCopiedAddition(false), 2000);
			Analytics.actions.interactions.gdsCopyAction({
				bookingId: booking.id,
				editedString: isEditing ? editedString : '',
				gdsType,
				generatedString,
				isEditing: isEditing,
				user: profile,
				additionSegment: editedStringEA?.trim(),
				additionText: generatedAddition,
			});
		} catch (err) {
			console.error('Failed to copy text: ', err);
		}
	};

    const highlightRoomType = (text, description) => {
        const roomType = normalizeToSabreRoomType(description);

        const roomTypeMatch = text.match(roomType);
        if (!roomTypeMatch) return text;

        const parts = text.split(roomTypeMatch[0]);
        return (
            <>
                {parts[0]}
                <span className="gds-highlight">{roomTypeMatch[0]}</span>
                {parts[1]}
            </>
        );
    };

    const getTooltipText = () => {
        return (
            <div>
                <div><b>1. Copy the Booking Script</b></div>
                <div style={{ marginBottom: '6px' }}>Click the Copy Booking button to copy the
                    entire {gdsType} GDS script to your clipboard.
                </div>
                <div><b>2. Open Your GDS</b></div>
                <div style={{ marginBottom: '6px' }}>Log in to your {gdsType} GDS environment. Open the
                    terminal (Command Page) where you usually enter booking commands.
                </div>
                <div><b>3. Paste and Send</b></div>
                <div style={{ marginBottom: '6px' }}>Paste the script (Ctrl+V or right-click → Paste) directly
                    into the command line and hit Enter. The booking data will be processed automatically.
                </div>
                {gdsType === SELECTED_TYPE.AMADEUS && (
                    <>
                        <div><b>Note on Amadeus Format:</b></div>
                        <div style={{ marginBottom: '6px' }}>
                            {booking?.hotel?.amadeus?.id ?
                                "Using Amadeus Hotel Code method which pulls full hotel details automatically." :
                                "Using Free-Flow Text method for hotels not found in the Amadeus database."}
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <BookingField
            fieldTitle={'Sync to GDS'}
            fieldValue={
                <div>
                    <div className='gds-description'>
                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className="edit"
                        >
                            <PencilIcon size={16} style={{ color: '#f8f8f8' }}/>
                        </button>
                        <span>
                            Copy your booking data into your <br/>
                            GDS system&nbsp;&nbsp;
                        </span>
                        <ExtrasTooltip arrow placement="left" title={getTooltipText()}>
                            <span className='gds-lern-more'>Learn more </span>
                        </ExtrasTooltip>
                    </div>

                    {isEditing && <div className={isEditing ? 'gds-string editing' : 'gds-string viewing'}>
                        {isEditing ? (
                            <textarea
                                ref={textareaRef}
                                value={editedString}
                                onChange={(e) => {
                                    setEditedString(e.target.value)
                                }}
                                className="text-box"
                            />
                        ) : (
                            <div>
                                {gdsType === SELECTED_TYPE.SABRE
                                    ? highlightRoomType(generatedString, booking.deal.details.room_description)
                                    : generatedString}
                            </div>
                        )}
                    </div>}

                    <div className='booking-reservation-summary breakdown'>
                        {isAmadeusEnabled && isSabroEnabled && <FormControl component="fieldset">
                            <RadioGroup
                                row
                                aria-label="gds-type"
                                name="gds-type"
                                value={gdsType}
                                onChange={(e) => setGdsType(e.target.value)}
                            >
                                <FormControlLabel
                                    value={SELECTED_TYPE.SABRE}
                                    control={<Radio style={{ paddingRight: '4px' }}/>}
                                    style={{ marginRight: '22px' }}
                                    label={
                                        <SabreLogo
                                            alt="Sabre Logo"
                                            style={{ width: '60px', height: 'auto' }}
                                        />
                                    }
                                />
                                <FormControlLabel
                                    value={SELECTED_TYPE.AMADEUS}
                                    control={<Radio style={{ paddingRight: '4px' }}/>}
                                    label={
                                        <AmadeusLogo
                                            alt="Amadeus Logo"
                                            style={{ width: '85px', height: 'auto' }}
                                        />
                                    }
                                />
                            </RadioGroup>
                        </FormControl>}

                        {!isAmadeusEnabled && isSabroEnabled && <SabreLogo
                            alt="Sabre Logo"
                            style={{ width: '60px', height: 'auto' }}
                        />}

                        {isAmadeusEnabled && !isSabroEnabled && <AmadeusLogo
                            alt="Amadeus Logo"
                            style={{ width: '85px', height: 'auto' }}
                        />}

                        <Button
                            className="gds-copy-button"
                            color={copied ? 'success' : 'primary'}
                            variant={copied ? 'contained' : 'outlined'}
                            onClick={handleCopyClick}>
                            {copied ?
                                <CheckIcon/> :
                                <CopyIcon/>}
                            {copied ? 'Copied!' : 'Copy Booking'}
                        </Button>
                    </div>
                    {isAmadeusEnabled && isEACompany && (
                        <div style={{ marginTop: '8px' }}>
							<div style={{ fontWeight: 500, lineHeight: '14px' }}>Enter Segment Number</div>
							<div style={{ fontSize: '11px', fontWeight: 300, marginBottom: '8px', lineHeight: '16px' }}> (We will prefix your segment number with 'S')</div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<div style={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(0,0,0,0.23)', borderRight: 'none', borderRadius: '4px 0 0 4px', padding: '6px 8px', color: 'rgba(0,0,0,0.6)', height: '36px' }}>S</div>
									<TextField
										placeholder="2"
										size="small"
										value={editedStringEA || ''}
										onChange={(e) => setEditedStringEA(e.target.value)}
										sx={{
											flex: 1,
											maxWidth: '100px',
											'& .MuiOutlinedInput-notchedOutline': {
												borderTopLeftRadius: 0,
												borderBottomLeftRadius: 0,
											},
										}}
									/>
								</div>
                                <Button
                                    className="gds-copy-button"
                                    color={copiedAddition ? 'success' : 'primary'}
                                    variant={copiedAddition ? 'contained' : 'outlined'}
                                    onClick={generateAdditionAndCopy}
                                    disabled={!editedStringEA || editedStringEA.trim() === ''}
                                >
                                    {copiedAddition ? <CheckIcon/> : <CopyIcon/>}
                                    {copiedAddition ? 'Copied!' : 'Copy Additions'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default BookingDrawerGDSComponent;
